import { useMemoizedFn, useMount } from 'ahooks';
import { Switch } from '@leadsnavi/ui-kit';
import mixpanel from 'mixpanel-browser';
import ReplyAgentLogoIcon from '@/assets/icons/reply-agent-logo.svg?react';
import { useConfirm } from '@/components/confirm-dialog';
import { useReplyAgentStore, getReplyAgentStatusText } from '@/stores/reply-agent-store';
import useSupplierStore from '@/stores/supplier-session';
import { useStore as useSupplierDetailStore } from '@/features/supplier-detail/store';
import { IApiSourcingContactEmail } from '@/services/swagger/easourcing/models';
import { IApiContactUpdateDetailDto } from '@/services/swagger/gator/models';

export default function ReplyAgentBanner() {
  const confirm = useConfirm();
  const enabled = useReplyAgentStore(state => state.enabled);
  const initAutoReplySetting = useReplyAgentStore(state => state.initAutoReplySetting);
  const toggling = useReplyAgentStore(state => state.toggling);
  const updateContact = useSupplierDetailStore(state => state.updateContact);
  const toggle = useReplyAgentStore(state => state.toggle);
  const syncAutoReplyStatus = useSupplierStore(state => state.syncAutoReplyStatus);
  useMount(() => {
    initAutoReplySetting();
  });
  const statusText = getReplyAgentStatusText(enabled);

  const handleToggle = useMemoizedFn(async () => {
    const nextEnabled = !enabled;

    const confirmed = await confirm({
      title: nextEnabled ? 'AI Auto-Reply Enabled' : 'AI Auto-Reply Disabled',
      message: nextEnabled
        ? 'The AI will now handle buyer inquiries. Unanswerable questions will create an issue.'
        : 'You will need to respond to all buyer messages manually.',
      okText: 'Confirm',
      cancelText: 'Cancel',
      showCancel: true,
    });

    if (!confirmed) return;

    mixpanel.track('suppliers_all_autoreply_click');
    try {
      await toggle();
      updateContact({
        autoReplyEnabled: nextEnabled ? 1 : 0,
      } as Partial<IApiSourcingContactEmail> as unknown as Partial<IApiContactUpdateDetailDto>);
      syncAutoReplyStatus();
    } catch {
      // toggle 内部已处理回滚与 toast，此处静默忽略
    }
  });

  return (
    <div className="relative   px-4  ">
      {/* 渐变背景 */}
      <div className="pointer-events-none absolute inset-0 opacity-90">
        <div
          className="absolute rounded-full"
          style={{
            width: 99,
            height: 99,
            bottom: 44,
            right: 0,
            opacity: 0.6,
            background: '#37E4A6',
            filter: 'blur(52px)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 87,
            height: 87,
            bottom: 30,
            left: '50%',
            opacity: 0.4,
            transform: 'translateX(-80%)',
            background: '#C4ED94',
            filter: 'blur(44px)',
          }}
        />
      </div>
      <div className="border-b border-[#E5E5E5] w-full flex items-center gap-2  pt-[22px] pb-[6px]">
        {/* AI Agent Logo（含光晕环，已内嵌在 SVG 中） */}
        <ReplyAgentLogoIcon className="flex-shrink-0 w-[50px] h-[50px]" />

        {/* 文字区域 */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#262626] leading-tight">Reply Agent</p>
          <p className="text-sm text-[#737373] leading-tight mt-0.5">{statusText}</p>
        </div>

        {/* Toggle 开关 */}
        <Switch
          checked={enabled}
          disabled={toggling}
          className={`shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]${enabled ? ' bg-[#059669]!' : ''}`}
          onCheckedChange={handleToggle}
          size="sm"
        />
      </div>
    </div>
  );
}
